// Lib
import React, { CSSProperties, RefObject } from 'react';
import { useSelector } from 'react-redux';
import { cx } from '@milanote/roma/utils/cx';

// Utils
import { stopPropagationOnly } from '../../../../../utils/domUtil';

// Selectors
import { currentBoardHasMediaDownloadAccessSelector } from '../../../../../utils/permissions/elementPermissionsSelector';
import { isPresentationModeEnabledSelector } from '../../../../../workspace/presentation/presentationSelector';

// Components
import ImageModalV2PrimaryTools from './ImageModalV2PrimaryTools';
import StickyPortalMount from '../../../../../components/animations/StickyPortalMount';

// Types
import { ImageModalV2Mode } from '../hooks/useImageModalV2Mode';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

// Constants
import { IMAGE_MODAL_SECONDARY_TOOLBAR_ID } from '../../imageModalConstants';

// Styles
import './ImageModalV2Toolbar.scss';

type ImageModalV2ToolbarProps = {
    element: ImMNElement;
    mode: ImageModalV2Mode;
    isEditable: boolean;
    canNavigate: boolean;
    navigateToPreviousImage: () => void;
    navigateToNextImage: () => void;
    setMode: (mode: ImageModalV2Mode) => void;
    isToolPanelOpen: boolean;
    style?: CSSProperties;
    primaryToolbarRef?: RefObject<HTMLDivElement>;
};

const ImageModalV2Toolbar: React.FC<ImageModalV2ToolbarProps> = ({
    element,
    mode,
    isEditable,
    canNavigate,
    navigateToPreviousImage,
    navigateToNextImage,
    setMode,
    isToolPanelOpen,
    style,
    primaryToolbarRef,
}) => {
    const showDownloadOption = useSelector(currentBoardHasMediaDownloadAccessSelector);
    const isPresentationMode = useSelector(isPresentationModeEnabledSelector);
    const showPrimary = !isToolPanelOpen;

    return (
        <div
            className="ImageModalV2Toolbar absolute right-0 left-0 z-[2] h-12 w-full pointer-events-none"
            style={style}
        >
            <div
                className={cx(
                    'ImageModalV2Toolbar-animation-container',
                    'w-full transition-transform duration-200 [transition-timing-function:cubic-bezier(0.3,1,0.7,1)]',
                    showPrimary ? 'translate-y-0' : '-translate-y-12',
                )}
            >
                <div
                    ref={primaryToolbarRef}
                    className={cx(
                        'ImageModalV2Toolbar-primary flex h-12 w-full items-center justify-center pointer-events-none',
                        !showPrimary && 'invisible',
                    )}
                >
                    <ImageModalV2PrimaryTools
                        element={element}
                        mode={mode}
                        isEditable={isEditable}
                        showDownloadOption={showDownloadOption}
                        isPresentationMode={isPresentationMode}
                        canNavigate={canNavigate}
                        navigateToPreviousImage={navigateToPreviousImage}
                        navigateToNextImage={navigateToNextImage}
                        setMode={setMode}
                    />
                </div>
                <div
                    className={cx(
                        'ImageModalV2Toolbar-secondary flex h-12 w-full items-end justify-center pointer-events-none',
                        showPrimary && 'invisible',
                    )}
                >
                    <StickyPortalMount
                        id={IMAGE_MODAL_SECONDARY_TOOLBAR_ID}
                        className="ImageModalV2SecondaryTools image-secondary-tools"
                        onMouseDown={stopPropagationOnly}
                        onClick={stopPropagationOnly}
                    />
                </div>
            </div>
        </div>
    );
};

export default ImageModalV2Toolbar;
